import { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BilledInBadge } from '@/components/ads/BillingCurrency';
import { useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { ArrowUpRight, BarChart3, Building2, Megaphone } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import './google-ads.css';

export function GoogleAdsMark() {
  return <svg viewBox="0 0 40 40" width="32" height="32" aria-hidden="true"><path d="M22 8 8 32" stroke="#fbbc04" strokeWidth="12" strokeLinecap="round" /><path d="m22 8 14 24" stroke="#4285f4" strokeWidth="12" strokeLinecap="round" /><circle cx="8" cy="32" r="6" fill="#34a853" /></svg>;
}

export function GoogleAdsShell({ section, title, description, actions, children }: {
  section: 'Account' | 'Campaigns' | 'Measurement'; title: string; description: string; actions?: ReactNode; children: ReactNode;
}) {
  const { auth } = useYoutubeAuth();
  const navigate = useNavigate();
  const connected = !!(auth?.connected && auth.customer_selected);
  return <div className="gads-module min-h-full">
    <Topbar title={`Google Ads · ${section}`} description={description} breadcrumb={`Google Ads / ${section}`} autoBack={false}
      right={<>
        {/* Google reports the customer's currency when the account is picked; re-picking it refreshes. */}
        {connected && <BilledInBadge platform="google" currency={auth?.currency} onRefresh={() => navigate('/google-ads/account')} />}
        <a href="https://ads.google.com/" target="_blank" rel="noreferrer" className="gads-external">Open Google Ads <ArrowUpRight size={14} /></a>
      </>} />
    <div className="gads-page">
      <nav className="gads-nav" aria-label="Google Ads workspace">
        {[
          { label: 'Account', to: '/google-ads/account', Icon: Building2 },
          { label: 'Campaigns', to: '/google-ads/campaigns', Icon: Megaphone },
          { label: 'Measurement', to: '/google-ads/tools', Icon: BarChart3 },
        ].map(({ label, to, Icon }) => <NavLink key={to} to={to} className={({ isActive }) => `gads-nav-link ${isActive ? 'is-active' : ''}`}><Icon size={17} />{label}</NavLink>)}
        {actions && <div className="gads-nav-actions">{actions}</div>}
      </nav>
      {children}
    </div>
  </div>;
}

export function GoogleAdsNotice({ title, children, error = false }: { title: string; children: ReactNode; error?: boolean }) {
  return <div role={error ? 'alert' : 'status'} className={`gads-notice ${error ? 'is-error' : ''}`}><strong>{title}</strong><div>{children}</div></div>;
}
